import { Modal, useMediaQuery } from "@mui/material";
import React, { useState } from "react";
import { CheckCircle, Feather } from "react-feather";
import API from "../../../axios/instances/API";
import TableLoader from "../../../components/Loaders/TableLoader";
import AcceptDarModal from "../../../components/Modals/AcceptDarModal";
import ConfirmModalSmall from "../../../components/Modals/ConfirmModalSmall";
import ActionButtonForTables from "../../../components/Table/ActionButtonForTables";
import CustomDataTableIcons from "../../../components/Table/CustomDataTableIcons";
import DateTooltip from "../../../components/Table/DateTooltip";
import DeleteButtonForTables from "../../../components/Table/DeleteButtonForTables";
import TableTitle from "../../../components/Table/TableTitle";
import { makeDefaultTableOptions } from "../../../storage/helpers";
import useAccessRequestLists from "../../../swr/hooks/DashboardAcceessRequest/useAccessRequestLists";
import { CustomDatatableV2 } from "../styles";
import useGlobalStore from "src/store/useGlobalStore";

const ACCESS_LEVELS = {
  READ_ONLY: "Read Only",
  ADMIN: "Admin",
  READ_ONLY_ORGANZINATION: "Read Only Organization Admin",
  ORGANIZATION: "Organization Admin",
  SUPERVISOR: "Supervisor",
};

const PendingAdminTable = ({ readOnly, setSnackbarText }) => {
  const refetchConfigBackground = useGlobalStore(
    (state) => state.refetchConfigBackground,
  );
  const [confirmInfo, setConfirmInfo] = useState(null);
  const [acceptInfo, setAcceptInfo] = useState(null);
  const isUnder600Px = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { data, isLoading, mutate } = useAccessRequestLists("C");

  const options = makeDefaultTableOptions("admin-access-requests");
  options.sortOrder = {
    name: "created",
    direction: "desc",
  };

  options.textLabels = {
    body: {
      noMatch:
        "There are no pending requests for admin access. Click “Invite Admin” to send an invite to a new admin from the button above!",
    },
  };

  options.rowsPerPage = 50;

  if (isUnder600Px) {
    options.rowsPerPageOptions = [];
    options.jumpToPage = false;
  }

  if (isLoading) return <TableLoader />;

  const handleRejectRequest = async () => {
    try {
      await API.delete("/dashboard/dashboard-access-request/details/", {
        data: {
          id: confirmInfo?.id,
        },
      });
      await mutate();
      await refetchConfigBackground();
      setSnackbarText(
        `Declined access request from ${confirmInfo?.name} warning`,
      );
      setConfirmInfo(null);
    } catch (error) {
      setSnackbarText("Failed to decline admin access request error");
      setConfirmInfo(null);
    }
  };

  const { access_requests } = data;

  const tableData = access_requests.map((req) => ({
    id: req.id,
    created: new Date(req.created).toISOString(),
    name: `${req.first_name} ${req.last_name}`,
    email: req.email,
  }));

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: false,
        display: false,
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "email",
      label: "Email",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "created",
      label: "Date Sent",
      options: {
        filter: true,
        sort: true,
        display: true,
        customBodyRender: (value) => (
          <DateTooltip date={value} shouldLocalize />
        ),
      },
    },
    {
      name: "accept",
      label: "Approve",
      options: {
        sort: false,
        filter: false,
        empty: true,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => (
          <div style={{ width: "fit-content" }}>
            <ActionButtonForTables
              Icon={CheckCircle}
              title="approve access request"
              disabled={readOnly}
              handleClick={() =>
                setAcceptInfo({
                  accessLevel: ACCESS_LEVELS.ADMIN,
                  id: tableMeta.rowData[0],
                  name: tableMeta.rowData[1],
                })
              }
            />
          </div>
        ),
      },
    },
    {
      name: "delete",
      label: "Decline",
      options: {
        sort: false,
        filter: false,
        empty: true,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => (
          <DeleteButtonForTables
            disabled={readOnly}
            handleClick={() =>
              setConfirmInfo({
                id: tableMeta.rowData[0],
                name: tableMeta.rowData[1],
              })
            }
          />
        ),
      },
    },
  ];

  return (
    <>
      <CustomDatatableV2
        title={<TableTitle title="Admin Access Requests" Icon={Feather} />}
        data={tableData}
        options={options}
        columns={columns}
        components={{
          icons: CustomDataTableIcons,
        }}
      />
      <Modal open={Boolean(confirmInfo)} onClose={() => setConfirmInfo(null)}>
        <div>
          <ConfirmModalSmall
            title="Decline Access Request"
            name={confirmInfo?.name}
            handleConfirm={handleRejectRequest}
          />
        </div>
      </Modal>
      <Modal open={Boolean(acceptInfo)} onClose={() => setAcceptInfo(null)}>
        <div>
          <AcceptDarModal
            name={acceptInfo?.name}
            id={acceptInfo?.id}
            accessLevel={ACCESS_LEVELS.ADMIN}
            setSnackbarText={setSnackbarText}
            handleClose={() => setAcceptInfo(null)}
            mutate={mutate}
          />
        </div>
      </Modal>
    </>
  );
};

export default PendingAdminTable;
